import { CSSProperties, FC, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useSnapshot } from 'valtio'
import { CurrentUserState } from '@app/storage'
import { useTawktoConfig } from '@app/hooks/useTawkto'
import {
  TAWK_SHELL_ID,
  mountingRef,
  computeChatPanelPlacement,
  injectHideDefaultTawkBubbleStyles,
  mountTawkWidgetInShell,
  type ChatPanelPlacement,
} from './positionTawkWidget'
import { setTawkChatSessionOpen, markTawkLoaded } from './supportChatApi'
import { SupportChatControls } from './SupportChatControls'
import { SupportChatSessionOverlay } from './SupportChatSessionOverlay'
import { isSupportChatLauncherVisible } from './supportChatRoutes'
import { isSupportChatMenuPosition, isVipSubscribeMode, getBubblePosition } from '@app/hooks/useTawkto'

declare global {
  interface Window {
    Tawk_API: {
      onLoad?: () => void
      onBeforeLoad?: () => void
      onChatMinimized?: () => void
      onChatMaximized?: () => void
      setAttributes?: (attrs: Record<string, unknown>, cb?: (err?: unknown) => void) => void
      hideWidget?: () => void
      showWidget?: () => void
      endChat?: () => void
      toggle?: () => void
      maximize?: () => void
      minimize?: () => void
      customStyle?: Record<string, unknown>
    }
    Tawk_LoadStart: Date
  }
}

const toShellStyle = (p: ChatPanelPlacement, visible: boolean): CSSProperties => ({
  position:     'fixed',
  bottom:        p.bottomPx,
  left:          p.leftPx,
  width:         p.widthPx,
  // topPx means mobile: fill from header to nav (height: auto)
  ...(p.topPx !== undefined ? { top: p.topPx } : { height: p.heightPx }),
  zIndex:        1001,
  borderRadius:  16,
  overflow:      'hidden',
  // transform creates a containing block for any position:fixed descendants.
  // If Tawk briefly resets #max-widget to position:fixed before our MO re-fills
  // it, overflow:hidden clips any bleed so the user never sees a flash.
  transform:     'translateZ(0)',
  display:       visible ? 'block' : 'none',
})

/**
 * Mounts once inside the router (after user context is available).
 * Injects the tawk.to embed script lazily — only when the tenant's
 * support_chat_tawkto flag is enabled.
 *
 * The widget (#max-widget) is reparented into our own shell <div> when opened
 * so that Tawk's internal style resets cannot change the panel's dimensions or
 * position — we only style the shell, which Tawk never touches.
 */
export const SupportChatProvider: FC = () => {
  const config   = useTawktoConfig()
  const location = useLocation()
  const { username, userEmail, id: userId } = useSnapshot(CurrentUserState)
  const loaded   = useRef(false)
  const [chatOpen, setChatOpen]     = useState(false)
  const [placement, setPlacement]   = useState(() => computeChatPanelPlacement())

  // Recompute shell position on resize.
  useLayoutEffect(() => {
    if (!config) return
    const update = () => setPlacement(computeChatPanelPlacement())
    window.addEventListener('resize', update, { passive: true })
    return () => window.removeEventListener('resize', update)
  }, [ config ])

  // Sync chatOpen with custom events from supportChatApi.
  useLayoutEffect(() => {
    const onOpen  = () => setChatOpen(true)
    const onClose = () => setChatOpen(false)
    window.addEventListener('tipsterizer-tawk-chat-opened', onOpen)
    window.addEventListener('tipsterizer-tawk-chat-closed', onClose)
    return () => {
      window.removeEventListener('tipsterizer-tawk-chat-opened', onOpen)
      window.removeEventListener('tipsterizer-tawk-chat-closed', onClose)
    }
  }, [])

  // Inject hide-bubble styles (once, while config is active).
  useEffect(() => {
    if (!config) return
    return injectHideDefaultTawkBubbleStyles()
  }, [ config ])

  // Inject Tawk script and register callbacks (once per mount).
  useEffect(() => {
    if (!config || loaded.current) return
    loaded.current = true

    window.Tawk_API       = window.Tawk_API || {}
    window.Tawk_LoadStart = new Date()
    setTawkChatSessionOpen(false)

    window.Tawk_API.onBeforeLoad = () => {
      window.Tawk_API.hideWidget?.()
    }

    const prevOnLoad = window.Tawk_API.onLoad
    window.Tawk_API.onLoad = () => {
      prevOnLoad?.()
      markTawkLoaded()
      setTawkChatSessionOpen(false)
      window.Tawk_API.hideWidget?.()
    }

    window.Tawk_API.onChatMinimized = () => {
      if (mountingRef.current) {
        // This minimize was fired because we reparented #max-widget into the shell.
        // Tawk detects the DOM move and fires minimize — ignore it.
        console.info('[tawk-shell] onChatMinimized ignored (mounting in progress)')
        return
      }
      console.info('[tawk-shell] onChatMinimized — closing session')
      setTawkChatSessionOpen(false)
    }

    window.Tawk_API.onChatMaximized = () => {
      console.info('[tawk-shell] onChatMaximized — bodyAttr=', document.body.getAttribute('data-tawk-chat-open'))
      if (document.body.hasAttribute('data-tawk-chat-open')) {
        // Widget is already in the shell (set by openTawkSupportChat before maximize),
        // just make sure it's mounted and filling correctly.
        mountTawkWidgetInShell()
      } else {
        // Tawk maximized itself (e.g. via its own UI) without our launcher — hide it.
        window.Tawk_API.hideWidget?.()
      }
    }

    const s1 = document.createElement('script')
    const s0 = document.getElementsByTagName('script')[0]
    s1.async = true
    s1.src   = `https://embed.tawk.to/${config.propertyId}/${config.widgetId}`
    s1.charset = 'UTF-8'
    s1.setAttribute('crossorigin', '*')
    s0?.parentNode?.insertBefore(s1, s0)
  }, [ config ])

  // Set Tawk user attributes when user info changes.
  useEffect(() => {
    if (!config || !userId || !window.Tawk_API?.setAttributes) return
    window.Tawk_API.setAttributes(
      { name: username ?? '', email: userEmail ?? '', userId },
      (err) => { if (err) console.warn('[tawk.to] setAttributes error', err) },
    )
  }, [ config, userId, username, userEmail ])

  const hasMenuPosition   = config ? isSupportChatMenuPosition(config.positions) : false
  const bubblePosition    = config ? getBubblePosition(config.positions) : null
  const bubbleVisible     = bubblePosition != null && isSupportChatLauncherVisible(location.pathname, config)

  // Hide widget when navigating to a route where the launcher shouldn't be shown.
  useEffect(() => {
    if (!config || hasMenuPosition) return
    const visible = isSupportChatLauncherVisible(location.pathname, config)
    if (!visible) window.Tawk_API?.hideWidget?.()
  }, [ config, location.pathname, hasMenuPosition ])

  // End chat session on logout.
  useEffect(() => {
    if (!userId && window.Tawk_API?.endChat) {
      window.Tawk_API.endChat()
    }
  }, [ userId ])

  if (!config) return null

  // Both a menu/overlay chat AND a floating bubble can be active simultaneously
  // e.g. positions: ["bottom-right-above-menu", "vip-subscribe"]
  return (
    <>
      {/* Shell div — Tawk's #max-widget is reparented here when opened. */}
      <div id={TAWK_SHELL_ID} style={toShellStyle(placement, chatOpen)} />
      {hasMenuPosition && <SupportChatSessionOverlay />}
      {bubbleVisible && <SupportChatControls position={bubblePosition} />}
    </>
  )
}
